import React from 'react';
import {
  Box,
  Typography,
  Paper,
  Grid,
  Divider,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Chip,
  IconButton,
  Button,
  Card,
  CircularProgress
} from '@mui/material';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import KitchenIcon from '@mui/icons-material/Kitchen';
import CalendarTodayIcon from '@mui/icons-material/CalendarToday';
import PersonIcon from '@mui/icons-material/Person';
import DescriptionIcon from '@mui/icons-material/Description';
import { useParams, useNavigate } from 'react-router-dom';
import { useGetConsumptionByIdQuery } from '../../../store/api/inventoryApi';

function ConsumptionDetail() {
  const { id } = useParams();
  const navigate = useNavigate();
  const { data: consumption, isLoading, error } = useGetConsumptionByIdQuery(id);

  if (isLoading) return (
    <Box sx={{ display: 'flex', justifyContent: 'center', p: 4 }}>
      <CircularProgress sx={{ color: '#F57C00' }} />
    </Box>
  );

  if (error) return (
    <Box sx={{ p: 4 }}>
      <Typography color="error">Error loading consumption record.</Typography>
    </Box>
  );

  return (
    <Box sx={{ p: 3 }}>
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 3 }}>
        <IconButton onClick={() => navigate('/inventory/consumptions')} sx={{ color: '#F57C00' }}>
          <ArrowBackIcon />
        </IconButton>
        <Typography variant="h4" sx={{ fontWeight: 700, color: '#2C3E50' }}>
          Consumption Details: {consumption.consumptionNumber}
        </Typography>
      </Box>

      <Grid container spacing={3}>
        {/* Info Cards */}
        <Grid size={{ xs: 12, md: 4 }}>
          <Card sx={{ height: '100%', borderRadius: 3, boxShadow: '0 4px 20px rgba(0,0,0,0.05)' }}>
            <Box sx={{ p: 3 }}>
              <Typography variant="subtitle2" color="text.secondary" gutterBottom>LOCATION & DATE</Typography>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 2 }}>
                <KitchenIcon sx={{ color: '#F57C00' }} />
                <Typography variant="h6">{consumption.location?.name}</Typography>
              </Box>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                <CalendarTodayIcon sx={{ color: '#F57C00' }} />
                <Typography>{new Date(consumption.date).toLocaleDateString()}</Typography>
              </Box>
            </Box>
          </Card>
        </Grid>

        <Grid size={{ xs: 12, md: 4 }}>
          <Card sx={{ height: '100%', borderRadius: 3, boxShadow: '0 4px 20px rgba(0,0,0,0.05)' }}>
            <Box sx={{ p: 3 }}>
              <Typography variant="subtitle2" color="text.secondary" gutterBottom>TOTAL VALUATION</Typography>
              <Typography variant="h4" sx={{ fontWeight: 700, color: '#F57C00', mt: 1 }}>
                {Number(consumption.totalCost).toLocaleString(undefined, { minimumFractionDigits: 2 })} AED
              </Typography>
              <Typography variant="body2" color="text.secondary">Based on FIFO batch costs</Typography>
            </Box>
          </Card>
        </Grid>

        <Grid size={{ xs: 12, md: 4 }}>
          <Card sx={{ height: '100%', borderRadius: 3, boxShadow: '0 4px 20px rgba(0,0,0,0.05)' }}>
            <Box sx={{ p: 3 }}>
              <Typography variant="subtitle2" color="text.secondary" gutterBottom>LOGGED BY</Typography>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mt: 1 }}>
                <PersonIcon sx={{ color: '#F57C00' }} />
                <Typography variant="h6">{consumption.creator?.firstName} {consumption.creator?.lastName}</Typography>
              </Box>
            </Box>
          </Card>
        </Grid>

        {/* Notes */}
        {consumption.notes && (
          <Grid size={{ xs: 12 }}>
            <Card sx={{ borderRadius: 3, boxShadow: '0 4px 20px rgba(0,0,0,0.05)' }}>
              <Box sx={{ p: 2, display: 'flex', alignItems: 'flex-start', gap: 2 }}>
                <DescriptionIcon sx={{ color: 'text.secondary', mt: 0.5 }} />
                <Box>
                  <Typography variant="subtitle2" color="text.secondary">Notes / Reason</Typography>
                  <Typography>{consumption.notes}</Typography>
                </Box>
              </Box>
            </Card>
          </Grid>
        )}

        {/* Items Table */}
        <Grid size={{ xs: 12 }}>
          <TableContainer component={Paper} sx={{ borderRadius: 3, boxShadow: '0 4px 20px rgba(0,0,0,0.05)' }}>
            <Box sx={{ p: 2, borderBottom: '1px solid rgba(0,0,0,0.05)', backgroundColor: '#F8F9FA' }}>
              <Typography variant="h6" sx={{ fontWeight: 600 }}>Consumed Items</Typography>
            </Box>
            <Table>
              <TableHead>
                <TableRow>
                  <TableCell sx={{ fontWeight: 600 }}>Product Name</TableCell>
                  <TableCell sx={{ fontWeight: 600 }}>SKU</TableCell>
                  <TableCell align="center" sx={{ fontWeight: 600 }}>Quantity Used</TableCell>
                  <TableCell align="right" sx={{ fontWeight: 600 }}>Unit Cost (FIFO)</TableCell>
                  <TableCell align="right" sx={{ fontWeight: 600 }}>Subtotal</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {consumption.items?.map((item) => (
                  <TableRow key={item.id} hover>
                    <TableCell sx={{ fontWeight: 600 }}>{item.product?.name}</TableCell>
                    <TableCell>{item.product?.sku}</TableCell>
                    <TableCell align="center">
                      <Chip label={`${item.quantity} ${item.product?.unit || 'Kg'}`} color="warning" variant="outlined" size="small" />
                    </TableCell>
                    <TableCell align="right">{Number(item.unitCost).toLocaleString(undefined, { minimumFractionDigits: 2 })}</TableCell>
                    <TableCell align="right" sx={{ fontWeight: 700 }}>
                      {Number(item.subtotal).toLocaleString(undefined, { minimumFractionDigits: 2 })} AED
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        </Grid>
      </Grid>
    </Box>
  );
}

export default ConsumptionDetail;
